import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RoleSettingsDrawer from "./RoleSettingsDrawer";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import DeleteWarningModal from "./DeleteWarningModal";
import ImportRolesModal from "./ImportRolesModal";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

import {
  CERTIFICATE_TYPE_LABELS,
  buildCertificateTypeLabels,
} from "../../../utilities/certificateTypes";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_certificate_types } from "../../../constants";

const NON_COVERAGE_LABELS = {
  training: "Training",
  admin: "Admin",
};

function RoleSettingsDisplay(props) {
  const hasStations = Array.isArray(props.stations) && props.stations.length > 0;

  const default_data = {
    role_name: null,
    role_type: null,
    role_type_id: null,
    station_options: [],
    exclude_from_labor: false,
    non_coverage_classification: null,
    required_certificate_types: [],
  };

  const [certificateTypes, setCertificateTypes] = React.useState([]);

  React.useEffect(() => {
    const fetchCertificateTypes = async () => {
      if (!props.homeStore) return;
      try {
        const res = await getRightworkAPI(API_get_certificate_types, {
          store_id: props.homeStore,
        });
        if (res.status === 200) {
          setCertificateTypes(res.data?.redux?.payload?.certificate_types || []);
        }
      } catch (err) {
        console.error("Failed to fetch certificate types:", err);
      }
    };
    fetchCertificateTypes();
  }, [props.homeStore]);

  const certificateTypeLabelLookup = React.useMemo(
    () => buildCertificateTypeLabels(certificateTypes),
    [certificateTypes]
  );

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [modalData, setModalData] = React.useState(default_data);
  const [modalEdit, setModalEdit] = React.useState(false);

  const [openDeleteWarningModal, setOpenDeleteWarningModal] =
    React.useState(false);

  const [selectedRole, setSelectedRole] = React.useState(null);
  const [roleDeleteImpact, setRoleDeleteImpact] = React.useState(null);
  const [roleDeleteReplacementId, setRoleDeleteReplacementId] = React.useState("");
  const [isRoleDeleteImpactLoading, setIsRoleDeleteImpactLoading] = React.useState(false);

  // POS Modal state
  const [posModalOpen, setPosModalOpen] = React.useState(false);
  const [suggestOnOpen, setSuggestOnOpen] = React.useState(false);
  const [mappingRole, setMappingRole] = React.useState(null);

  const handlePosModalOpen = (role = null) => {
    setMappingRole(role ? { id: role.id, name: role.role_name } : null);
    setSuggestOnOpen(false);
    setPosModalOpen(true);
  };
  const handlePosModalClose = () => setPosModalOpen(false);
  const handleOpenDeleteWarningModal = async (roleToDelete) => {
    setSelectedRole(roleToDelete);
    setRoleDeleteImpact(null);
    setRoleDeleteReplacementId("");
    setOpenDeleteWarningModal(true);

    if (!roleToDelete?.id || !props.get_role_delete_impact) {
      return;
    }

    setIsRoleDeleteImpactLoading(true);
    const impactPayload = await props.get_role_delete_impact(roleToDelete.id);
    setRoleDeleteImpact(impactPayload);
    setIsRoleDeleteImpactLoading(false);
  };
  const handleCloseDeleteWarningModal = () => {
    setOpenDeleteWarningModal(false);
    setRoleDeleteImpact(null);
    setRoleDeleteReplacementId("");
    setSelectedRole(null);
  };
  React.useEffect(() => {
    if (!props.autoOpenImportModal) {
      return;
    }
    setMappingRole(null);
    setSuggestOnOpen(props.autoOpenImportModal === "suggest");
    setPosModalOpen(true);
    props.onAutoOpenHandled?.();
  }, [props.autoOpenImportModal, props.onAutoOpenHandled]);

  const unmappedPosRoles = React.useMemo(
    () => (Array.isArray(props.posRoles) ? props.posRoles.filter((posRole) => !posRole.role_id) : []),
    [props.posRoles]
  );

  const mappedPosRolesByRoleId = React.useMemo(() => {
    const grouped = {};
    (props.posRoles || []).forEach((posRole) => {
      if (!posRole.role_id) {
        return;
      }
      if (!grouped[posRole.role_id]) {
        grouped[posRole.role_id] = [];
      }
      grouped[posRole.role_id].push(posRole);
    });
    return grouped;
  }, [props.posRoles]);

  const sortedRoles = React.useMemo(
    () =>
      (Array.isArray(props.roles) ? props.roles.slice() : []).sort((a, b) => {
        if (a.role_type_id === b.role_type_id) {
          return a.role_name.localeCompare(b.role_name);
        }
        return a.role_type_id > b.role_type_id ? 1 : -1;
      }),
    [props.roles]
  );

  const [orderedRoles, setOrderedRoles] = React.useState(
    Array.isArray(props.roles) ? props.roles : []
  );
  const [isReordered, setIsReordered] = React.useState(false);

  React.useEffect(() => {
    setOrderedRoles(Array.isArray(props.roles) ? props.roles : []);
    setIsReordered(false);
  }, [props.roles]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const next = Array.from(orderedRoles);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrderedRoles(next);
    setIsReordered(true);
  };

  const handleSaveOrder = async () => {
    if (!props.role_set_order) return;
    const payload = orderedRoles.map((r, index) => ({
      role_id: r.id,
      sort_index: index,
    }));
    const wasSuccessful = await props.role_set_order(payload);
    if (wasSuccessful) {
      setIsReordered(false);
    }
  };

  const deleteReplacementRoleOptions = React.useMemo(
    () => sortedRoles.filter((roleOption) => roleOption.id !== selectedRole?.id),
    [selectedRole?.id, sortedRoles]
  );

  React.useEffect(() => {
    if (
      roleDeleteImpact?.requires_reassignment &&
      !roleDeleteReplacementId &&
      deleteReplacementRoleOptions.length > 0
    ) {
      setRoleDeleteReplacementId(String(deleteReplacementRoleOptions[0].id));
    }
  }, [deleteReplacementRoleOptions, roleDeleteImpact?.requires_reassignment, roleDeleteReplacementId]);

  const renderLaborSettings = (role) => {
    if (!role.exclude_from_labor) {
      return <span className="text-slate-400">Included</span>;
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        <div className="rounded border border-orange-200 bg-orange-100 px-2 py-1 text-orange-700">
          Excluded
        </div>
        {role.non_coverage_classification && (
          <div className="rounded border border-slate-200 bg-slate-100 px-2 py-1 text-slate-700">
            {NON_COVERAGE_LABELS[role.non_coverage_classification] || ""}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* <SettingsHeader title="Role Settings" /> */}

      <div className="w-full py-4 flex flex-wrap items-center justify-between gap-3">
        {/* POS mapping status */}
        {(props.posRoles?.length > 0 || props.excludedPosRoles?.length > 0) && (
          <div className="min-w-0 text-sm">
            <div className={`inline-flex min-h-[36px] flex-wrap items-center gap-2 py-1.5 rounded-lg border px-3 ${unmappedPosRoles.length ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
              {unmappedPosRoles.length ? <WarningIcon style={{ fontSize: "17px" }} /> : <CheckCircleOutlineIcon style={{ fontSize: "17px" }} />}
              <span className="font-medium">{`${unmappedPosRoles.length} unmapped POS ${unmappedPosRoles.length === 1 ? "job" : "jobs"}`}</span>
              <button
                type="button"
                className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-medium text-inherit hover:bg-white"
                onClick={() => handlePosModalOpen()}
              >
                POS job mappings
              </button>
            </div>
          </div>
        )}

        {props.permissionRole !== "company_manager" && <Tooltip title="Add role">
          <button
            className="ml-auto shrink-0 bg-violet-700 hover:bg-violet-800 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
            onClick={() => {
              setModalData(default_data);
              setModalEdit(false);
              handleOpen();
            }}
          >
            <AddIcon style={{ fontSize: "18px" }} /><span>Add role</span>
          </button>
        </Tooltip>}
      </div>

      <div className="flex flex-col">
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <div className="min-w-full">
            <div className="overflow-hidden">
             <DragDropContext onDragEnd={onDragEnd}>
              <table className="min-w-full table-fixed divide-y divide-slate-200">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className={`py-3.5 pl-6 pr-3 text-left text-xs text-slate-500 font-medium ${
                        hasStations ? "w-[20%]" : "w-[26%]"
                      }`}
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className={`px-3 py-3.5 text-left text-xs text-slate-500 font-medium ${
                        hasStations ? "w-[12%]" : "w-[14%]"
                      }`}
                    >
                      Department
                    </th>
                    {hasStations && (
                      <th
                        scope="col"
                        className="w-[20%] px-3 py-3.5 text-left text-xs text-slate-500 font-medium"
                      >
                        Stations
                      </th>
                    )}
                    <th
                      scope="col"
                      className={`px-3 py-3.5 text-left text-xs text-slate-500 font-medium ${
                        hasStations ? "w-[16%]" : "w-[18%]"
                      }`}
                    >
                      Required certificates
                    </th>
                    <th
                      scope="col"
                      className={`px-3 py-3.5 text-left text-xs text-slate-500 font-medium ${
                        hasStations ? "w-[16%]" : "w-[22%]"
                      }`}
                    >
                      Labor settings
                    </th>
                    <th
                      scope="col"
                      className="w-[12%] relative py-3.5 pl-3 pr-6 text-end text-sm font-medium"
                    >
                      {props.companyScope ? <span className="text-xs text-slate-500">Actions</span> : isReordered ? (
                        <Tooltip title="Save this order of roles. Roles will show up in this order on the scheduling view when sorting or grouping by role.">
                          <div className="w-full flex justify-end">
                            <div
                              className="px-4 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full cursor-pointer text-xs whitespace-nowrap"
                              onClick={handleSaveOrder}
                            >
                              Save Order
                            </div>
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Drag and drop roles to reorder how they appear on the scheduling view when sorting or grouping by role.">
                          <div className="w-full flex justify-end">
                            <div className="px-3 py-1 bg-slate-200 text-slate-400 rounded-full text-xs whitespace-nowrap">
                              Save Order
                            </div>
                          </div>
                        </Tooltip>
                      )}
                    </th>
                  </tr>
                </thead>
                <Droppable droppableId="roles">
                 {(droppableProvided) => (
                <tbody
                  className="divide-y divide-slate-200 bg-white"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {Array.isArray(props.roles) && orderedRoles.length === 0 && <tr>
                    <td colSpan={hasStations ? 6 : 5} className="px-6 py-10 text-center">
                      <h3 className="text-sm font-semibold text-slate-900">No roles yet</h3>
                      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                        Roles define the work people are scheduled to do on a shift.{" "}
                        {props.posRoles?.length > 0
                          ? props.permissionRole === "company_manager" ? "Map your POS jobs to get started." : "Add a role or map your POS jobs to get started."
                          : props.permissionRole === "company_manager" ? "A company administrator can add roles for your team." : "Add your first role to get started."}
                      </p>
                    </td>
                  </tr>}
                  {orderedRoles.map((role, index) => (
                    <Draggable
                      key={String(role.id)}
                      draggableId={String(role.id)}
                      index={index}
                      isDragDisabled={Boolean(props.companyScope)}
                    >
                     {(provided, snapshot) => (
                        <tr
                          key={role.id}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            background: snapshot.isDragging ? "#f8fafc" : undefined,
                            display: snapshot.isDragging ? "table" : undefined,
                            tableLayout: snapshot.isDragging ? "fixed" : undefined,
                          }}
                        >
                          <td
                            className="py-5 pl-6 pr-3 text-sm align-middle"
                            style={snapshot.isDragging ? { width: hasStations ? 240 : 312 } : undefined}
                          >
                            <div className="flex items-center gap-2">
                              {!props.companyScope && <div
                                {...provided.dragHandleProps}
                                className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
                                aria-label="Drag to reorder"
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </div>}
                              {(mappedPosRolesByRoleId[role.id] || []).length > 0 ? (
                                <Tooltip
                                  title={`View ${(mappedPosRolesByRoleId[role.id] || []).length} mapped POS job${
                                    (mappedPosRolesByRoleId[role.id] || []).length === 1 ? "" : "s"
                                  }`}
                                  placement="top"
                                >
                                  <button
                                    type="button"
                                    onClick={() => handlePosModalOpen(role)}
                                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1"
                                    aria-label={`View mapped POS jobs for ${role.role_name}`}
                                  >
                                    <LinkIcon fontSize="inherit" className="text-emerald-600" />
                                  </button>
                                </Tooltip>
                              ) : (
                                <Tooltip title="No POS role mapped" placement="top">
                                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-400">
                                    <LinkOffIcon fontSize="inherit" />
                                  </span>
                                </Tooltip>
                              )}
                              <div className="w-fit">
                                <div
                                  className={`rounded px-3 p-1 ${
                                    role.color ? "text-white" : ""
                                  } ${role.color}`}
                                >
                                  {role.role_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td
                            className="px-3 py-5 text-sm text-slate-900 align-middle"
                            style={snapshot.isDragging ? { width: hasStations ? 144 : 168 } : undefined}
                          >
                            <div>{role.role_type}</div>
                          </td>
                          {hasStations && (
                            <td
                              className="px-3 py-5 text-sm text-slate-900 align-middle"
                              style={snapshot.isDragging ? { width: 240 } : undefined}
                            >
                              {role.station_options?.length > 0 ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  {role.station_options.map((station, j) => (
                                    <div
                                      key={j}
                                      className="rounded bg-violet-100 px-3 py-1 text-violet-700"
                                    >
                                      {station.station_name}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">None</span>
                              )}
                            </td>
                          )}
                          <td
                            className="px-3 py-5 text-sm text-slate-900 align-middle"
                            style={snapshot.isDragging ? { width: hasStations ? 192 : 216 } : undefined}
                          >
                            {role.required_certificate_types?.length > 0 ? (
                              <Tooltip
                                placement="top"
                                title={
                                  <div className="py-1">
                                    {role.required_certificate_types.map((certType) => (
                                      <div key={certType}>
                                        {certificateTypeLabelLookup[certType] ||
                                          CERTIFICATE_TYPE_LABELS[certType] ||
                                          certType}
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <div className="inline-flex cursor-default rounded border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
                                  {role.required_certificate_types.length}
                                </div>
                              </Tooltip>
                            ) : (
                              <span className="text-slate-400">None</span>
                            )}
                          </td>
                          <td
                            className="px-3 py-5 text-sm text-slate-900 align-middle"
                            style={snapshot.isDragging ? { width: hasStations ? 192 : 264 } : undefined}
                          >
                            {renderLaborSettings(role)}
                          </td>
                          <td
                            className="relative py-5 pl-3 pr-6 text-right text-sm font-medium align-middle"
                            style={snapshot.isDragging ? { width: 144 } : undefined}
                          >
                            <div className="flex items-center justify-end gap-3">
                              <Tooltip title="Edit">
                                <IconButton
                                  className="text-slate-500"
                                  sx={{ backgroundColor: "#f8fafc" }}
                                  onClick={() => {
                                    setModalData(role);
                                    setModalEdit(true);
                                    handleOpen();
                                  }}
                                >
                                  <EditOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                              {props.permissionRole !== "company_manager" && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    className="text-slate-500"
                                    sx={{ backgroundColor: "#f8fafc" }}
                                    onClick={() => {
                                      setSelectedRole(role);
                                      handleOpenDeleteWarningModal(role);
                                    }}
                                  >
                                    <DeleteOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                     )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </tbody>
                 )}
                </Droppable>
              </table>
             </DragDropContext>
              <div className="border-b border-slate-200"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Roles Modal */}
      <ImportRolesModal
        open={posModalOpen}
        autoSuggest={suggestOnOpen}
        initialRole={mappingRole}
        onClose={handlePosModalClose}
        companyId={props.companyId}
        onSaved={props.onMappingsSaved}
      />

      <RoleSettingsDrawer
        open={open}
        onClose={handleClose}
        modalData={modalData}
        modalEdit={modalEdit}
        departments={props.departments}
        edit_role={props.edit_role}
        isSavingData={props.isSavingData}
        store_name={props.store_name}
        companyScope={props.companyScope}
        permissionRole={props.permissionRole}
        certificateTypes={certificateTypes}
      />
      <DeleteWarningModal
        type="role"
        open={openDeleteWarningModal}
        handleClose={handleCloseDeleteWarningModal}
        delete_function_role={props.edit_role}
        selectedItem={selectedRole}
        availableRoles={deleteReplacementRoleOptions}
        deleteImpact={roleDeleteImpact}
        deleteImpactLoading={isRoleDeleteImpactLoading}
        replacementRoleId={roleDeleteReplacementId}
        onReplacementRoleChange={setRoleDeleteReplacementId}
      />
    </div>
  );
}

export default RoleSettingsDisplay;
